import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useSyncExternalStore } from "react";
import type { UserPublic } from "shared/schemas";
import { ME_QUERY_KEY, patchUserSettings } from "./use-auth.js";

// BACKLOG.md "Der Feedback-Knopf erklärt nicht, was er tut": two independent
// opt-outs around the "Falsch markiert?" span-feedback button.
// `modalDismissed` skips the one-time explainer; `feedbackDisabled` removes
// the button entirely. Same "anonymize.*" localStorage namespace and same
// module-level-store shape as use-theme.ts/use-marker-format.ts, so every
// mounted ConfidenceMarker (one per span, span-renderer.tsx) sees a checkbox
// checked in ANY of them reflected everywhere without a page reload.
const MODAL_DISMISSED_KEY = "anonymize.feedbackModalDismissed";
const FEEDBACK_DISABLED_KEY = "anonymize.feedbackDisabled";

function readBool(key: string): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(key) === "true";
}

let modalDismissed = readBool(MODAL_DISMISSED_KEY);
let feedbackDisabled = readBool(FEEDBACK_DISABLED_KEY);
const listeners = new Set<() => void>();

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getModalDismissedSnapshot(): boolean {
  return modalDismissed;
}

function getFeedbackDisabledSnapshot(): boolean {
  return feedbackDisabled;
}

// Safe default for the SSR/pre-hydration snapshot: assume neither opt-out is
// set, same "least surprising, most permissive" choice getServerSnapshot
// makes in use-theme.ts/use-marker-format.ts.
function getServerSnapshot(): boolean {
  return false;
}

function applyAndNotify(): void {
  for (const l of listeners) l();
}

/**
 * Mirror a value into the in-memory store WITHOUT touching localStorage —
 * used by the login-time sync below, whose whole point is to apply an
 * asymmetric merge rule rather than a plain write. See
 * writeThemeLocal/writeMarkerFormatLocal for the cross-tab-ping-pong
 * rationale this mirrors (not applicable here today - these two booleans
 * have no cross-tab listener wired - but keeping the same shape avoids a
 * silent divergence if one gets added later).
 */
export function applyModalDismissedLocal(next: boolean): void {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(MODAL_DISMISSED_KEY, String(next));
  }
  modalDismissed = next;
  applyAndNotify();
}

export function applyFeedbackDisabledLocal(next: boolean): void {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(FEEDBACK_DISABLED_KEY, String(next));
  }
  feedbackDisabled = next;
  applyAndNotify();
}

/**
 * The "Falsch markiert?" feedback preferences: whether the one-time
 * explainer modal has been dismissed, and whether the feedback button is
 * disabled entirely. Local-first (works for every tier, including anon,
 * even though the button itself only ever renders Docs+ - see
 * ConfidenceMarkerProps.tier in span-renderer.tsx), with an explicit PATCH
 * to `/users/me` whenever a session exists - same "local write always,
 * server write only when logged in" shape as useMarkerFormat/useTheme.
 *
 * Deliberately NOT the same login-time merge those two hooks use.
 * useUserSettingsSync mirrors user.theme/user.markerFormat into local
 * UNCONDITIONALLY on login - correct there because every value is equally
 * valid and the account is simply the more durable copy. These two booleans
 * are not symmetric: one says "stop explaining/asking", and a device that
 * already said so must not start asking again just because a DIFFERENT
 * account (or the same account, from a session before it was set) is now
 * signed in on it. The account explicitly re-enabling one (Account
 * settings) is the one path that DOES write `false` both ways - see
 * setModalDismissed/setFeedbackDisabled below. The asymmetric login-time
 * adoption rule lives in use-user-settings-sync.ts.
 */
export function useFeedbackPreferences(): {
  modalDismissed: boolean;
  feedbackDisabled: boolean;
  setModalDismissed: (next: boolean) => void;
  setFeedbackDisabled: (next: boolean) => void;
} {
  const dismissed = useSyncExternalStore(subscribe, getModalDismissedSnapshot, getServerSnapshot);
  const disabled = useSyncExternalStore(subscribe, getFeedbackDisabledSnapshot, getServerSnapshot);
  const qc = useQueryClient();

  const setModalDismissed = useCallback(
    (next: boolean) => {
      applyModalDismissedLocal(next);
      const user = qc.getQueryData<UserPublic | null>(ME_QUERY_KEY);
      if (user && user.feedbackModalDismissed !== next) {
        void patchUserSettings(qc, { feedbackModalDismissed: next });
      }
    },
    [qc],
  );

  const setFeedbackDisabled = useCallback(
    (next: boolean) => {
      applyFeedbackDisabledLocal(next);
      const user = qc.getQueryData<UserPublic | null>(ME_QUERY_KEY);
      if (user && user.feedbackDisabled !== next) {
        void patchUserSettings(qc, { feedbackDisabled: next });
      }
    },
    [qc],
  );

  return {
    modalDismissed: dismissed,
    feedbackDisabled: disabled,
    setModalDismissed,
    setFeedbackDisabled,
  };
}
